import SectionLabel from "./SectionLabel";
import SmartLink from "./SmartLink";
import { experienceCards } from "../data/availability";
import Img from "./Img";

const descriptions: Record<string, string> = {
  Refuge: "Partner with organizations to help integrate refugees into new communities.",
  Preserve: "Partner with organizations to create sustainable resources and/or protect the environment.",
  Build: "Support local development efforts by contributing to infrastructure projects. Schools, homes, community buildings — the flagship HXP experience.",
  Teach: "Practice and teach conversational skills in English teaching schools.",
  Love: "Partner with organizations to help support marginalized communities.",
};

/* Wordmarks: recolored by marketing Oct 25 (Build red, Refuge black, no NEW! badges),
   trimmed to the artwork so equal CSS heights render equal words. Every side word is
   153px tall in source; Preserve is a long word, so `wide` lifts the max-w-full clamp
   and lets it overflow into the tile padding instead of shrinking. */
const logos: Record<string, { src: string; wide?: boolean }> = {
  Refuge: { src: "/images/experiences/refuge-black.png" },
  Preserve: { src: "/images/experiences/preserve.png", wide: true },
  Build: { src: "/images/experiences/build.png" },
  Teach: { src: "/images/experiences/teach.png" },
  Love: { src: "/images/experiences/love-2026.png" },
};

const cards = experienceCards.map((c) => ({
  name: c.name,
  image: logos[c.name]?.src ?? c.image,
  desc: descriptions[c.name] ?? c.caption,
}));

// Symmetric row: two tiles — BUILD (featured, center) — two tiles
const ordered = [
  cards.find((c) => c.name === "Refuge")!,
  cards.find((c) => c.name === "Preserve")!,
  cards.find((c) => c.name === "Build")!,
  cards.find((c) => c.name === "Teach")!,
  cards.find((c) => c.name === "Love")!,
];

const ExperiencesSection = () => (
  <section id="experiences" className="border-y border-hxp-sand-dark bg-hxp-cream py-20 md:py-24">
    <div className="mx-auto max-w-[1200px] px-6">
      <SectionLabel>Our Experiences</SectionLabel>
      <h2 className="mt-6 max-w-3xl text-4xl md:text-5xl">
        Choose the experience that's <span className="text-primary">right for you</span>
      </h2>

      <div className="mt-12 grid grid-cols-1 items-stretch gap-px overflow-hidden rounded-sm border border-hxp-sand-dark bg-hxp-sand-dark sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1.6fr_1fr_1fr]">
        {ordered.map((exp) => {
          const featured = exp.name === "Build";
          return (
            <SmartLink
              key={exp.name}
              href="/availability/"
              className={`group flex flex-col justify-center bg-background transition-colors hover:bg-foreground ${
                featured ? "p-8 sm:col-span-2 lg:col-span-1 lg:p-10" : "p-6"
              }`}
            >
              <div
                className={`mx-auto flex w-full justify-center ${
                  featured ? "h-20 items-center sm:h-28 lg:h-40" : "h-10 items-end sm:h-[59px]"
                }`}
              >
                <Img
                  sizes="240px"
                  src={exp.image}
                  alt={exp.name}
                  loading="lazy"
                  className={`w-auto object-contain transition-all duration-300 group-hover:scale-105 group-hover:brightness-0 group-hover:invert ${
                    logos[exp.name]?.wide ? "max-w-full lg:max-w-none" : "max-w-full"
                  } ${featured ? "h-full" : "h-7 sm:h-10"}`}
                />
              </div>
              {/* Fixed-height text box on sm+ so every side tile's content block is
                  the same height — logos and first text lines align exactly. */}
              <p
                className={`font-body mt-4 leading-relaxed text-muted-foreground transition-colors group-hover:text-background/80 ${
                  featured ? "text-center text-base" : "text-sm sm:h-[128px]"
                }`}
              >
                {exp.desc}
              </p>
            </SmartLink>
          );
        })}
      </div>
    </div>
  </section>
);

export default ExperiencesSection;
